import { useCallback, useMemo, useRef, useState } from 'react';
import Map, { Layer, Popup, Source } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import config from '@/config';
import PropTypes from 'prop-types';
import { GlobeAltIcon } from '@heroicons/react/24/outline';

const MAP_STYLE = 'mapbox://styles/mapbox/light-v11';

// Maps our CSV region names to Mapbox country-boundaries-v1 name_en values.
// Regions set to null are super-regions (continents, blocs) with no single country shape.
const REGION_TO_MAPBOX = {
  'United States of America': 'United States',
  'Republic of Korea': 'South Korea',
  'Russian Federation': 'Russia',
  'Viet Nam': 'Vietnam',
  Turkiye: 'Turkey',
  Africa: null,
  Asia: null,
  Europe: null,
  Eurasia: null,
  Oceania: null,
  'Central America & Caribbean': null,
  'Other Asia': null,
  'Other North America': null,
  'Other South America': null,
};

function toMapboxName(region) {
  if (region in REGION_TO_MAPBOX) return REGION_TO_MAPBOX[region];
  return region;
}

const DataviewerMap = ({ rows }) => {
  const mapRef = useRef(null);
  const [popup, setPopup] = useState(null);

  // Mapbox name → rows (for click lookup)
  const rowsByMapboxName = useMemo(() => {
    const m = {};
    rows.forEach(r => {
      if (!r.region) return;
      const name = toMapboxName(r.region);
      if (name) (m[name] ??= []).push(r);
    });
    return m;
  }, [rows]);

  const coveredNames = useMemo(() => Object.keys(rowsByMapboxName), [rowsByMapboxName]);

  // Rows whose region is a super-region (continent, bloc) with no country shape
  const superRegionRows = useMemo(() => rows.filter(r => r.region && toMapboxName(r.region) === null), [rows]);

  // No worldview filter — some countries (e.g. China) only appear under a
  // country-specific worldview, not 'all', so filtering to 'all' hides them.
  const coveredFilter = useMemo(() => ['in', ['get', 'name_en'], ['literal', coveredNames]], [coveredNames]);

  const uncoveredFilter = useMemo(() => ['!', ['in', ['get', 'name_en'], ['literal', coveredNames]]], [coveredNames]);

  const handleClick = useCallback(
    e => {
      const feature = e.features?.[0];
      if (!feature) return;
      const name = feature.properties?.name_en;
      const matchingRows = rowsByMapboxName[name];
      if (!matchingRows) return;
      setPopup({ longitude: e.lngLat.lng, latitude: e.lngLat.lat, rows: matchingRows });
    },
    [rowsByMapboxName],
  );

  if (!config.mapBoxAccessToken) {
    return (
      <div className="flex flex-col items-center justify-center h-80 gap-3 text-gray-400 bg-gray-50 rounded-lg border border-gray-200">
        <GlobeAltIcon className="h-10 w-10" />
        <p className="text-sm">Mapbox token not configured.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden border border-gray-200">
      <Map
        ref={mapRef}
        mapboxAccessToken={config.mapBoxAccessToken}
        initialViewState={{ longitude: -98, latitude: 38, zoom: 2.5 }}
        style={{ width: '100%', height: 480 }}
        mapStyle={MAP_STYLE}
        interactiveLayerIds={['countries-covered', 'countries-uncovered']}
        onClick={handleClick}
        cursor="auto"
      >
        <Source id="countries" type="vector" url="mapbox://mapbox.country-boundaries-v1">
          <Layer
            id="countries-uncovered"
            type="fill"
            source-layer="country_boundaries"
            filter={uncoveredFilter}
            paint={{ 'fill-color': '#e5e7eb', 'fill-opacity': 0.5 }}
          />
          <Layer
            id="countries-covered"
            type="fill"
            source-layer="country_boundaries"
            filter={coveredFilter}
            paint={{ 'fill-color': '#22c55e', 'fill-opacity': 0.65 }}
          />
          <Layer
            id="countries-border"
            type="line"
            source-layer="country_boundaries"
            paint={{ 'line-color': '#ffffff', 'line-width': 0.5 }}
          />
        </Source>

        {popup && (
          <Popup
            longitude={popup.longitude}
            latitude={popup.latitude}
            anchor="top-left"
            onClose={() => setPopup(null)}
            closeButton={true}
            closeOnClick={false}
          >
            <div className="p-1 min-w-36 text-sm">
              <p className="font-semibold mb-1">{popup.rows[0].region}</p>
              {popup.rows.length === 1 ? (
                <>
                  <p className="text-xs text-gray-500 mb-1">{popup.rows[0].source}</p>
                  {popup.rows[0].capital_cost != null && (
                    <p className="mt-1">
                      Capex: <span className="font-mono">{Number(popup.rows[0].capital_cost).toLocaleString()}</span>
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-500">{popup.rows.length} records available</p>
              )}
            </div>
          </Popup>
        )}
      </Map>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 bg-gray-50 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-green-500" /> Data available
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-gray-200" /> No data
        </span>
        {superRegionRows.length > 0 && (
          <span className="text-gray-400">
            Also available for: {[...new Set(superRegionRows.map(r => r.region))].join(', ')}
          </span>
        )}
      </div>
    </div>
  );
};

DataviewerMap.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default DataviewerMap;
